import React, { useState } from "react";
import { AlertTriangle, X, ShieldAlert, CheckCircle2 } from "lucide-react";
import api from "../lib/api";

const REASONS = [
  { key: "tive_problemas_atendimento", label: "Tive problemas no atendimento (WhatsApp travou/atrasei)", isExecution: true },
  { key: "demorei_para_responder", label: "Demorei para responder os clientes nos momentos de pico", isExecution: true },
  { key: "nao_trabalhei_leads", label: "Não trabalhei o funil nem fiz quebra de objeções adequada", isExecution: true },
  { key: "nao_fiz_followup", label: "Não realizei o follow-up / remarcar conversa com quem parou", isExecution: true },
  { key: "leads_ruins", label: "Acho que os leads que chegaram eram desqualificados/ruins", isExecution: false },
  { key: "criativo_fraco", label: "O criativo não despertou interesse real do público", isExecution: false },
  { key: "campanha_fraca", label: "A campanha/algoritmo do Meta Ads parece fraco", isExecution: false },
];

export default function AssumeRiskModal({ isOpen, onClose, campaignId, onSuccess }) {
  const [selectedReason, setSelectedReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  if (!isOpen) return null;

  const selectedObj = REASONS.find((r) => r.key === selectedReason);
  const isEligible = selectedObj?.isExecution || false;

  const handleConfirm = async () => {
    if (!selectedReason) return;
    if (!isEligible) {
      setErrorMsg("A metodologia não permite extensão quando o problema é o criativo ou público. Nesse caso, a recomendação correta é CRIAR UM NOVO CRIATIVO.");
      return;
    }
    setLoading(true);
    setErrorMsg("");
    try {
      await api.post("/api/campaigns/assume-risk", {
        campaign_id: campaignId || "active",
        reason_key: selectedReason,
      });
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error("Risk error:", err);
      setErrorMsg(err.response?.data?.detail || "Erro ao assumir risco.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="assume-risk-modal-overlay"
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
    >
      <div
        data-testid="assume-risk-modal"
        className="bg-[#161822] border-2 border-red-500/50 rounded-2xl w-full max-w-lg p-6 relative shadow-[0_10px_50px_rgba(239,68,68,0.25)] text-left"
      >
        <button
          data-testid="close-risk-modal-btn"
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white p-1"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-red-500/20 border border-red-500/50 flex items-center justify-center text-red-400">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white leading-tight">
              Por que você acredita que não vendeu?
            </h2>
            <span className="text-xs text-gray-400">
              Diagnóstico sincero da operação antes de estender o prazo
            </span>
          </div>
        </div>

        <p className="text-xs text-gray-300 mb-3 leading-relaxed">
          A extensão de tolerância (+2 dias) só é concedida se o motivo foi falha de execução pessoal no atendimento. Seja 100% transparente:
        </p>

        <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
          {REASONS.map((r) => {
            const isSelected = selectedReason === r.key;
            return (
              <button
                type="button"
                key={r.key}
                data-testid={`risk-reason-btn-${r.key}`}
                onClick={() => {
                  setSelectedReason(r.key);
                  setErrorMsg("");
                }}
                className={`w-full text-left p-2.5 rounded-lg border text-xs transition-all flex items-start justify-between gap-2 ${
                  isSelected
                    ? "bg-red-500/15 border-red-500 text-white font-semibold"
                    : "bg-[#1C202C] border-[#2A3042] text-gray-300 hover:bg-[#232838]"
                }`}
              >
                <span>{r.label}</span>
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded font-mono shrink-0 ${
                    r.isExecution
                      ? "bg-amber-500/20 text-amber-300 border border-amber-500/30"
                      : "bg-gray-800 text-gray-400"
                  }`}
                >
                  {r.isExecution ? "Execução Pessoal" : "Produto/Criativo"}
                </span>
              </button>
            );
          })}
        </div>

        {errorMsg && (
          <div className="mt-3 p-2.5 bg-red-500/15 border border-red-500/40 rounded-lg text-xs text-red-300 font-medium flex items-start gap-2">
            <ShieldAlert className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
            <span>{errorMsg}</span>
          </div>
        )}

        {selectedReason && isEligible && (
          <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-xs text-amber-200">
            ⚠️ <strong>Você está assumindo o risco</strong> de manter este criativo ativo por mais 2 dias. Comprometa-se a corrigir o atendimento imediatamente.
          </div>
        )}

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg bg-[#222736] hover:bg-[#2B3144] text-gray-300 font-bold text-xs"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={!selectedReason || loading}
            data-testid="confirm-assume-risk-btn"
            onClick={handleConfirm}
            className={`flex-1 py-2.5 rounded-lg font-extrabold text-xs uppercase tracking-wider transition-all ${
              isEligible
                ? "bg-red-600 hover:bg-red-500 text-white shadow-[0_0_15px_rgba(239,68,68,0.4)]"
                : "bg-gray-700 text-gray-400 cursor-not-allowed"
            }`}
          >
            {loading ? "Gravando..." : "ASSUMIR RISCO (+2 DIAS)"}
          </button>
        </div>
      </div>
    </div>
  );
}
